import { useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { ParcelCard, RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'

/**
 * Setting and clearing exception markers on the parcel detail — ticket 22, spec
 * §2/§13, ADR-0005. A marker records a problem ALONGSIDE the Parcel's custody
 * state, never inside it: an operator sets `wrong-point` with a
 * required reason, and the custody state does not change.
 * A `wrong-point` Parcel routes onto the next return's working list.
 *
 * The reason is **required** (a marker is never silent), so each button stays
 * disabled until a marker and a reason are chosen. The set/clear calls are
 * injected (`setMarker`/`clearMarker`) so they are the stubbed tRPC seam in tests
 * (spec Testing Decisions). The card opens this form in a dialog from its «⋯» menu. Every operator-facing string goes through the
 * key-based i18n lookup; Russian is the only shipped language (spec §12).
 */

type MarkerInput = RouterInputs['markers']['Set']
type Marker = MarkerInput['marker']
type SetResult = RouterOutputs['markers']['Set']
type ClearResult = RouterOutputs['markers']['Clear']
/** Every marker a Parcel may carry — a partner decision's among them (ticket 40), set only by the server. */
type CarriedMarker = ParcelCard['markers'][number]

/** The markers an operator sets and clears by hand (ticket 22). `unknown-owner` is set
 * by its own flow; `damaged` only by the damage act, with a comment, for good (ticket 42). */
const OPERATOR_MARKERS: Marker[] = ['wrong-point']

async function setViaTrpc(input: MarkerInput): Promise<SetResult> {
	return trpcClient.markers.Set.mutate(input)
}
async function clearViaTrpc(input: MarkerInput): Promise<ClearResult> {
	return trpcClient.markers.Clear.mutate(input)
}

export interface MarkersPanelProps {
	parcelId: string
	/** The markers the Parcel currently carries — shown plainly, distinct from state. */
	markers: CarriedMarker[]
	/** Test seam: the set-marker call. Defaults to the real tRPC client. */
	setMarker?: (input: MarkerInput) => Promise<SetResult>
	/** Test seam: the clear-marker call. Defaults to the real tRPC client. */
	clearMarker?: (input: MarkerInput) => Promise<ClearResult>
	/** Called after a successful set or clear, so the detail can refetch. */
	onChanged?: () => void
}

export function MarkersPanel({
	parcelId,
	markers,
	setMarker = setViaTrpc,
	clearMarker = clearViaTrpc,
	onChanged,
}: MarkersPanelProps) {
	const { t } = useTranslate()
	const [marker, setMarkerValue] = useState<Marker | ''>('')
	const [note, setNote] = useState('')
	const [busy, setBusy] = useState(false)
	// The i18n KEY of the current error, not its translated text, so the flag stays
	// truthy even when a message resolves to an empty string.
	const [errorKey, setErrorKey] = useState<string | null>(null)

	const trimmed = note.trim()
	const ready = marker !== '' && trimmed !== ''

	const run = async (
		call: (input: MarkerInput) => Promise<SetResult | ClearResult>,
		failedKey: string,
	) => {
		if (!ready) {
			setErrorKey('markers.noteRequired')
			return
		}
		setBusy(true)
		setErrorKey(null)
		try {
			await call({ parcelId, marker: marker as Marker, note: trimmed })
			setNote('')
			onChanged?.()
		} catch {
			setErrorKey(failedKey)
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className='grid gap-4' data-testid='markers-panel'>
			<div className='grid gap-1.5'>
				<span className='text-xs text-muted-foreground'>
					{t('markers.current')}
				</span>
				{markers.length === 0 ? (
					<span className='text-sm' data-testid='markers-none'>
						{t('markers.none')}
					</span>
				) : (
					<div className='flex flex-wrap gap-1' data-testid='markers-current'>
						{markers.map((mk) => (
							<Badge key={mk} variant='destructive'>
								{t(`parcelMarker.${mk}` as never)}
							</Badge>
						))}
					</div>
				)}
			</div>

			<div className='grid gap-1.5'>
				<Label>{t('markers.markerLabel')}</Label>
				<div className='flex flex-wrap gap-2'>
					{OPERATOR_MARKERS.map((mk) => (
						<Button
							key={mk}
							type='button'
							size='sm'
							variant={marker === mk ? 'default' : 'outline'}
							data-testid={`marker-option-${mk}`}
							aria-pressed={marker === mk}
							onClick={() => setMarkerValue(mk)}
						>
							{t(`parcelMarker.${mk}` as never)}
						</Button>
					))}
				</div>
			</div>

			<div className='grid gap-1.5'>
				<Label htmlFor='marker-note'>{t('markers.note')}</Label>
				<Textarea
					id='marker-note'
					data-testid='marker-note'
					value={note}
					onChange={(e) => setNote(e.target.value)}
					placeholder={t('markers.notePlaceholder')}
				/>
			</div>

			<div className='flex items-center gap-2'>
				<Button
					type='button'
					data-testid='marker-set'
					disabled={busy || !ready}
					onClick={() => run(setMarker, 'markers.setFailed')}
				>
					{t('markers.set')}
				</Button>
				<Button
					type='button'
					variant='outline'
					data-testid='marker-clear'
					disabled={busy || !ready}
					onClick={() => run(clearMarker, 'markers.clearFailed')}
				>
					{t('markers.clear')}
				</Button>
				{errorKey && (
					<span
						className='text-sm text-destructive'
						data-testid='markers-error'
					>
						{t(errorKey as never)}
					</span>
				)}
			</div>
		</div>
	)
}
